﻿<!--
  BentoValue — asymmetric value grid for the employer landing page.
  Replaces the old centered stat-strip + 3 icon cards with one intentional bento.
  Dark surfaces, hairline borders, hover = border brighten + lift (no glow).
  Counter tiles emit [data-counter]/[data-format]; the page's IntersectionObserver
  animates them on reveal (see EmployerLandingPage animateCounter).
  Icon is globally registered (createJobsApp); isClient guards SSR counter display.
-->
<template>
  <div class="bento">
    <!-- Big stat -->
    <div class="bento-tile bento-tile--feature t-stat" data-reveal>
      <div class="bento-num" data-counter="100000" data-format="millions">{{ isClient ? '0' : '100K+' }}</div>
      <div class="bento-cap">Credentials evaluated since 2008. This is the community your applicants come from.</div>
    </div>

    <!-- Countries + dotted texture -->
    <div class="bento-tile t-countries" data-reveal>
      <div class="bento-dots" aria-hidden="true"></div>
      <div class="bento-num bento-num--sm" data-counter="190" data-format="plus">{{ isClient ? '0' : '190+' }}</div>
      <div class="bento-cap">Countries represented across the talent pool.</div>
    </div>

    <!-- Pre-screened -->
    <div class="bento-tile t-screen" data-reveal>
      <Icon name="shield-check" :size="20" class="bento-ico" />
      <div class="bento-h">Applicants with evaluated credentials</div>
      <div class="bento-cap">Many candidates have already had their credentials evaluated by Scholaro. Talent pool search shows the U.S. equivalency, and candidates can order a full report to share with you.</div>
    </div>

    <!-- Visa filtering -->
    <div class="bento-tile t-visa" data-reveal>
      <Icon name="globe" :size="20" class="bento-ico" />
      <div class="bento-h">Work-authorization filtering, built in</div>
      <div class="bento-cap">Surface sponsorship-ready candidates before you open a conversation.</div>
    </div>

    <!-- USCIS -->
    <div class="bento-tile t-uscis" data-reveal>
      <Icon name="badge-check" :size="20" class="bento-ico" />
      <div class="bento-h">Designed to meet USCIS requirements</div>
      <div class="bento-cap">AICE-member reports, on demand.</div>
    </div>

    <!-- Pricing. Both variants ship; the billing state class on the landing page
         root picks one (see EmployerLandingPage.vue). -->
    <div class="bento-tile t-free" data-reveal>
      <Icon name="circle-dollar-sign" :size="20" class="bento-ico" />
      <div class="bento-h jb-when-free">Free during early access</div>
      <div class="bento-cap jb-when-free">No card, no annual contract, no pay-to-promote.</div>
      <div class="bento-h jb-when-paid">One flat plan</div>
      <div class="bento-cap jb-when-paid">$299 a month for 1 job post, with applicants and talent pool search included. No annual contract, no pay-to-promote.</div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'BentoValue',
  setup() {
    return { isClient: typeof window !== 'undefined' };
  },
});
</script>

<style scoped>
.bento {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-auto-rows: minmax(120px, auto);
  gap: 14px;
}
.bento-tile {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(var(--lp-fg), 0.1);
  background: var(--lp-panel, rgba(var(--lp-fg), 0.03));
  border-radius: 18px;
  padding: 26px;
  transition: border-color 0.25s ease, transform 0.25s ease, background 0.25s ease;
  /* reveal */
  opacity: 0;
  transform: translateY(14px);
}
.bento-tile.is-visible { opacity: 1; transform: translateY(0); }
.bento-tile:hover {
  border-color: rgba(var(--jb-primary-rgb), 0.55);
  background: color-mix(in srgb, var(--lp-panel, #1A1F36) 88%, var(--lp-tint, #fff));
  transform: translateY(-2px);
}

/* spans (lg) */
.t-stat      { grid-column: span 3; grid-row: span 2; display: flex; flex-direction: column; justify-content: flex-end; }
.t-countries { grid-column: span 3; }
.t-screen    { grid-column: span 3; }
.t-visa      { grid-column: span 2; }
.t-uscis     { grid-column: span 2; }
.t-free      { grid-column: span 2; }

.bento-num {
  font-family: var(--jb-font-mono, ui-monospace, monospace);
  font-size: clamp(2.75rem, 6vw, 4.5rem);
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.02em;
  color: rgb(var(--lp-fg));
}
.bento-num--sm { font-size: clamp(2rem, 4vw, 3rem); }
.bento-h {
  margin-top: 14px;
  font-size: 1.0625rem;
  font-weight: 700;
  color: rgb(var(--lp-fg));
}
.bento-cap {
  margin-top: 10px;
  font-size: 0.875rem;
  line-height: 1.55;
  color: rgba(var(--lp-fg), 0.6);
  max-width: 42ch;
}
.bento-ico { color: rgba(var(--jb-primary-rgb), 1); }

/* crisp dotted texture (not a glow) for the countries tile */
.bento-dots {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(rgba(var(--jb-primary-rgb), 0.35) 1px, transparent 1.4px);
  background-size: 14px 14px;
  -webkit-mask-image: linear-gradient(to bottom right, #000, transparent 75%);
  mask-image: linear-gradient(to bottom right, #000, transparent 75%);
  opacity: 0.5;
  pointer-events: none;
}

@media (max-width: 900px) {
  .bento { grid-template-columns: repeat(2, 1fr); }
  .t-stat, .t-countries, .t-screen, .t-visa, .t-uscis, .t-free { grid-column: span 2; grid-row: auto; }
}
@media (prefers-reduced-motion: reduce) {
  .bento-tile { opacity: 1; transform: none; transition: border-color 0.2s ease; }
}
</style>
